import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Row = Record<string, string>;

function normalize(results: any[]): Row | null {
  if (!Array.isArray(results) || results.length === 0) return null;
  const obj = results[0];
  if (!obj || typeof obj !== 'object') return null;
  const toS = (v: any) => (v === null || v === undefined || v === '' ? '-' : String(v));
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, toS(v)]));
}

/**
 * Prefijos en mayúsculas para deducir el tipo desde el texto SPOOFING_TYPE
 * (coinciden con el código Python de Spoofy)
 */
const TYPE_PREFIXES: Array<{ type: string; prefix: string }> = [
  { type: '0', prefix: 'SPOOFING POSSIBLE FOR' },
  { type: '1', prefix: 'SUBDOMAIN SPOOFING POSSIBLE FOR' },
  { type: '2', prefix: 'ORGANIZATIONAL DOMAIN SPOOFING POSSIBLE FOR' },
  { type: '3', prefix: 'SPOOFING MIGHT BE POSSIBLE FOR' },
  { type: '4', prefix: 'SPOOFING MIGHT BE POSSIBLE (MAILBOX DEPENDENT)' },
  { type: '5', prefix: 'ORGANIZATIONAL DOMAIN SPOOFING MIGHT BE POSSIBLE (MAILBOX DEPENDENT)' },
  { type: '6', prefix: 'SUBDOMAIN SPOOFING MIGHT BE POSSIBLE (MAILBOX DEPENDENT)' },
  { type: '7', prefix: 'SUBDOMAIN SPOOFING IS POSSIBLE AND ORGANIZATIONAL DOMAIN SPOOFING MIGHT BE POSSIBLE FOR' },
  { type: '8', prefix: 'SPOOFING IS NOT POSSIBLE FOR' },
];

/** Mapea type -> clave de traducción para la frase que quieres mostrar */
const TYPE_I18N_KEY: Record<string, string> = {
  '0': 'tools.results_scan.spoofornot.type.0',
  '1': 'tools.results_scan.spoofornot.type.1',
  '2': 'tools.results_scan.spoofornot.type.2',
  '3': 'tools.results_scan.spoofornot.type.3',
  '4': 'tools.results_scan.spoofornot.type.4',
  '5': 'tools.results_scan.spoofornot.type.5',
  '6': 'tools.results_scan.spoofornot.type.6',
  '7': 'tools.results_scan.spoofornot.type.7',
  '8': 'tools.results_scan.spoofornot.type.8',
};

/**
 * Deducción del type:
 * 1) si spoofing_possible ∈ ['0'..'8'] úsalo
 * 2) si no, intenta con el texto spoofing_type por prefijo
 */
function deduceType(data: Row): string | null {
  const raw = (data.spoofing_possible ?? '').toString().trim();
  if (/^[0-8]$/.test(raw)) return raw;

  const st = (data.spoofing_type ?? '').toString().trim().toUpperCase();
  if (st) {
    const found = TYPE_PREFIXES.find(p => st.startsWith(p.prefix));
    if (found) return found.type;
  }
  return null;
}

/**
 * Considera suplantable si:
 * - score es null/undefined/'' o "none"
 * - score numérico > 0
 * No suplantable si score numérico === 0
 */
function riskStyle(scoreRaw: any) {
  const raw = (scoreRaw ?? '').toString().trim().toLowerCase();

  // null/undefined/vacío/"none" => riesgo
  if (raw === '' || raw === 'none' || raw === 'null') {
    return {
      labelKey: 'tools.results_scan.spoofornot.risk',
      classes: 'bg-rose-50 text-rose-700 ring-rose-200',
      risky: true,
    };
  }

  const n = Number(raw);
  if (!Number.isNaN(n)) {
    if (n > 0) {
      return {
        labelKey: 'tools.results_scan.spoofornot.risk',
        classes: 'bg-rose-50 text-rose-700 ring-rose-200',
        risky: true,
      };
    }
    // n === 0
    return {
      labelKey: 'tools.results_scan.spoofornot.ok',
      classes: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      risky: false,
    };
  }

  // Cualquier otro valor no numérico => tratamos como riesgo
  return {
    labelKey: 'tools.results_scan.spoofornot.risk',
    classes: 'bg-rose-50 text-rose-700 ring-rose-200',
    risky: true,
  };
}

const SpoofOrNotResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const data = normalize(results);

  if (!data) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.spoofornot.empty')}</p>;
  }

  const badge = riskStyle(data.spoofing_possible);
  const type = deduceType(data); // '0'..'8' | null
  const typeKey = (type && TYPE_I18N_KEY[type]) || 'tools.results_scan.spoofornot.type.unknown';

  const rows: Array<[string, string]> = [
    [t('tools.results_scan.spoofornot.domain'), data.domain || '-'],
    [t('tools.results_scan.spoofornot.spf'), data.spf || '-'],
    [t('tools.results_scan.spoofornot.spf_multiple_alls'), data.spf_multiple_alls || '-'],

    [t('tools.results_scan.spoofornot.dmarc'), data.dmarc || '-'],
    [t('tools.results_scan.spoofornot.dmarc_policy'), data.dmarc_policy || '-'],
    [t('tools.results_scan.spoofornot.dmarc_pct'), data.dmarc_pct || '-'],
    [t('tools.results_scan.spoofornot.dmarc_aspf'), data.dmarc_aspf || '-'],
    [t('tools.results_scan.spoofornot.dmarc_sp'), data.dmarc_sp || '-'],
    [t('tools.results_scan.spoofornot.dmarc_forensic_report'), data.dmarc_forensic_report || '-'],
    [t('tools.results_scan.spoofornot.dmarc_aggregate_report'), data.dmarc_aggregate_report || '-'],

    [t('tools.results_scan.spoofornot.bimi_record'), data.bimi_record || '-'],
    [t('tools.results_scan.spoofornot.bimi_version'), data.bimi_version || '-'],
    [t('tools.results_scan.spoofornot.bimi_location'), data.bimi_location || '-'],
    [t('tools.results_scan.spoofornot.bimi_authority'), data.bimi_authority || '-'],
  ];

  return (
    <div className="w-full">
      {/* Estado grande y centrado */}
      <div className="mb-3 flex justify-center">
        <span
          className={`inline-flex items-center rounded-full px-5 py-2 ring-1 font-semibold text-lg md:text-xl ${badge.classes}`}
          aria-live="polite"
        >
          {t(badge.labelKey)}
        </span>
      </div>

      {/* Frase/explicación debajo del estado */}
      <div className="mb-6 text-center">
        <p className="text-sm md:text-base text-slate-700">
          {t(typeKey, { domain: data.domain || '' })}
        </p>
      </div>

      {/* Tabla detallada */}
      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gradient-to-r from-sky-50 to-indigo-50">
            <tr className="text-slate-700">
              <th className="px-3 py-2 text-left uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.spoofornot.field')}
              </th>
              <th className="px-3 py-2 text-left uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.spoofornot.value')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map(([k, v], i) => (
              <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                <td className="px-3 py-2 font-medium text-slate-900">{k}</td>
                <td className="px-3 py-2 text-slate-700 break-words">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpoofOrNotResults;
